import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  PageHeader,
  DetailCard,
  InfoRow,
  LoadingState,
  StatusChip,
} from '../components';
import { useBulkImport } from '../hooks/useBulkImport';
import {
  colors,
  formatCurrency,
  formatDateTime,
  IMPORT_ACTION_CHIP,
} from '../constants';

const LIST_PATH = '/inventory-management/import';

/** Read-only view of one past import run and the lines it applied. */
export default function ImportDetail() {
  const { uuid } = useParams();
  const { data: run, isLoading, error } = useBulkImport(uuid);

  if (isLoading) return <LoadingState />;

  if (error || !run) {
    return (
      <>
        <PageHeader title="Import" backTo={LIST_PATH} />
        <Typography variant="body2" sx={{ color: colors.error }}>
          {error?.response?.data?.message || 'This import could not be loaded.'}
        </Typography>
      </>
    );
  }

  const rows = run.rows || [];

  return (
    <>
      <PageHeader
        title="Import"
        subtitle={formatDateTime(run.createdAt)}
        backTo={LIST_PATH}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <DetailCard title="Summary">
            <InfoRow label="Status" value={<StatusChip status={run.status} />} />
            <InfoRow label="Items" value={run.totalRows} />
            <InfoRow label="Created" value={run.createdCount} />
            <InfoRow label="Updated" value={run.updatedCount} />
            <InfoRow label="Failed" value={run.failedCount} />
            <InfoRow label="Total quantity" value={run.totalQuantity} />
            <InfoRow
              label="Total cost"
              value={
                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.primary }}>
                  {formatCurrency(run.totalCost)}
                </Typography>
              }
            />
            <InfoRow label="Imported at" value={formatDateTime(run.createdAt)} />
          </DetailCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <DetailCard
            title={`Items (${rows.length})`}
            action={
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {run.createdCount > 0 && (
                  <Chip size="small" color="success" label={`${run.createdCount} created`} />
                )}
                {run.updatedCount > 0 && (
                  <Chip size="small" color="info" label={`${run.updatedCount} updated`} />
                )}
                {run.failedCount > 0 && (
                  <Chip size="small" color="error" label={`${run.failedCount} failed`} />
                )}
              </Stack>
            }
          >
            <Box sx={{ overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.row}>
                      <TableCell>{row.row + 1}</TableCell>
                      <TableCell>
                        {row.name}
                        {row.barcodes?.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: colors.gray500, display: 'block' }}
                          >
                            {row.barcodes.join(', ')}
                          </Typography>
                        )}
                        {row.error && (
                          <Typography
                            variant="caption"
                            sx={{ color: colors.error, display: 'block' }}
                          >
                            {row.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: colors.primary }}>{row.sku || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={IMPORT_ACTION_CHIP[row.action]?.color || 'default'}
                          label={IMPORT_ACTION_CHIP[row.action]?.label || row.action}
                        />
                      </TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(row.unitCost)}</TableCell>
                      <TableCell align="right">
                        {row.previousStock == null
                          ? '-'
                          : `${row.previousStock} \u2192 ${row.newStock}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </DetailCard>
        </Grid>
      </Grid>
    </>
  );
}
